import React, { FC, useEffect, useRef, useState } from "react";
import { Row, Col, Button } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";
import {
  SearchPanel,
  MessagePanel,
  MapPanel,
  HeaderPanel,
} from "components/Dashboard";
import "./Dashboard.scss";
import { useDispatch, useSelector } from "react-redux";
import Autocomplete from "react-google-autocomplete";
import RidePanel from "components/Dashboard/RidePanel";
import FilteredPanel from "components/Dashboard/FilteredPanel";
import { isMapBucket, resolveActiveBucket } from "utils/messageBuckets";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  getPagesRequestPublishTopic,
  getPagesSubscriptionTopic,
  getPageSubscriptionTopic,
  getPageRequestPublishTopic,
  getUserSubscriptionTopic,
  getCreatePageSubscriptionTopic,
  getCreatePagePublishTopic,
  getUpdatePageSubscriptionTopic,
  getProjectsSubscriptionTopic,
  getProjectsRequestPublishTopic,
  getPageFocusSubscriptionTopic,
} from "hooks/MQTTConstants";
import { GET_USERS_LIST } from "reducers/authReducer";
import CallNotification from "containers/Video/CallNotification";
import { formatForRedis, generateUUID, popupCenter } from "utils/appUtil";
import VideoPlayerModal from "components/Modals/VideoPlayerModal";
import { PeopleModal } from "components/Modals";
import FirstCADIframeModal from "components/Modals/FirstCADIframeModal";
import { RootState } from "reducers/rootReducer";
import { NetworkClient } from "models/network";
import { DashboardTabs, SocketTopics, UserTopics } from "../../constants/enums";
import { pushNotification } from "utils/notifications";
import { GET_USER_INFO, GET_USER_AGGREGATE_INFO } from "reducers/authReducer";
import { TOKEN, RID } from "../../constants/localStoreKeys";
import { ENCRYPT_TOKEN } from "reducers/authReducer";

import { getMembersPageListPath } from "../../helpers/request/RequestPath";
import {
  getItemListRequestPayload,
  getPageSearchArgument,
  wrapRequestPayload,
} from "../../helpers/request/RequestPayload";

import useLocalStorageService from "../../services/localStorage";

import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import useSocketActions from "../../hooks/socketActions";
import { getFriendUserInfo } from "utils/chatUtils";
import { createPage } from "../../services/apis/collections/oAuthApis";

const firebaseConfig = {
  apiKey: "AIzaSyBaarBS5TpbG4uMpQTrAqspBVKbwzVkY7k",
  authDomain: "beam-live-passenger.firebaseapp.com",
  databaseURL: "https://beam-live-passenger.firebaseio.com",
  projectId: "beam-live-passenger",
  storageBucket: "beam-live-passenger.appspot.com",
  messagingSenderId: "391484580180",
  appId: "1:391484580180:web:8dca7ed0a3513fa42628c1",
  measurementId: "G-MBQ6J1T667",
};

const useMounted = () => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  return isMounted;
};

const Dashboard: FC<{ client: NetworkClient; socket: ISocket }> = ({
  client,
  socket,
}) => {
  const {
    searchResult,
    selectedItem,
    callNotification,
    twilioVideoURL,
    searchPage,
    searchArgs,
    friendSearchPage,
    focusItem,
    createdPageId,
  } = useSelector((state: RootState) => state.search);
  const isMobileScreen = useMediaQuery({ query: "(max-width: 700px)" });
  const [step, setStep] = useState<number>(DashboardTabs.Pages);
  const [selectedPageItem, setSelectedPageItem] =
    useState<SelectedSearchItem>();
  const [location, setLocation] = useState<any>();
  const [awaitingNewPage, setAwaitingNewPage] = useState<boolean>(false);
  const [addParticipantsVisible, setAddParticipantsVisible] =
    useState<boolean>(false);
  const { findUsersToPage, userBds, allUsers } = useSelector(
    (state: RootState) => state.auth
  );
  const { projects, requestIds } = useSelector(
    (state: RootState) => state.dashboard
  );
  const { messageFilter, messageBucket } = useSelector(
    (state: RootState) => state.message
  );

  /**
   * The right-hand column is the map for Places, GPS live, GPS reports and an
   * unfiltered page — the buckets whose result genuinely is a map. Every other
   * bucket shows its filtered result there instead, because a payments ledger
   * has nothing to plot and the map would just be taking up the space.
   */
  const activeBucket = resolveActiveBucket(
    messageBucket || "",
    messageFilter || []
  );
  const showMapColumn = isMapBucket(activeBucket);
  const history = useHistory();
  const { search } = useLocation();
  const dispatch = useDispatch();
  const { pageId } = useParams<{ pageId?: string }>();
  const [queryPageId, setQueryPageId] = useState<any>();
  const [callingPageId, setCallingPageId] = useState<string | undefined>(
    undefined
  );
  const [videoPlayerOpen, setVideoPlayerOpen] = useState<boolean>(false);
  const [firstCADVisible, setFirstCADVisible] = useState<boolean>(false);
  const [isCallNotificationDisplayed, setCallNotificaitonDisplayed] =
    useState<boolean>(false);
  const [screens, setScreens] = useState<ScreenStateObj>({
    list: false,
    message: false,
    iframe: false,
    map: false,
    active: "",
    ridePanel: false,
    cadType: "",
  });
  const isMounted = useMounted();
  const { getUserRequestPayload } = useSocketActions();
  const { getLocalStorageItem } = useLocalStorageService();

  const params: DashboardParams = {
    screens,
    setScreens,
    setFirstCADVisible,
  };

  /**
   * Picking a bucket the map cannot show has to open the right-hand column,
   * otherwise the filtered result is rendered into a column that is not
   * mounted and the filter looks like it did nothing.
   *
   * Keyed on the bucket *changing* rather than on the column being shut: a
   * reader who closes the panel while a filter is active gets to keep it
   * closed until they pick a different filter.
   */
  const lastBucket = useRef<string>();
  useEffect(() => {
    const changed = lastBucket.current !== activeBucket;
    lastBucket.current = activeBucket;
    if (!changed) return;
    if (!isMapBucket(activeBucket) && !screens.map && screens.message) {
      setScreens((prev) => ({ ...prev, map: true }));
    }
  }, [activeBucket, screens.map, screens.message]);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          setLocation({
            lat: position?.coords?.latitude,
            long: position?.coords?.longitude,
          });
        });
      } else {
        reject("Geolocation is not supported by this browser.");
      }
    });
  };

  useEffect(() => {
    if (twilioVideoURL) {
      setVideoPlayerOpen(true);
    }
  }, [twilioVideoURL]);

  const handleChangeStep = (sType: string) => {
    if (sType === "next" && step < DashboardTabs.Map) setStep(step + 1);
    if (sType === "before" && step > DashboardTabs.Pages) setStep(step - 1);
  };

  // Tracks friendUserIds that already have a friend-page create in flight, so
  // rapid double-clicks (or repeated SET_FRIEND_SEARCH_PAGE results that still
  // report no page yet) don't publish duplicate create-page events.
  const inFlightFriendPages = useRef<Set<string>>(new Set());

  const createFriendPage = async (friendUserId) => {
    const user = allUsers?.find(
      (obj) => obj.id === localStorage.getItem("RID")
    );
    const friendUser = allUsers?.find((obj) => obj.id === friendUserId);
    const timestamp = Date.now();
    var changes = [];
    changes.push({
      op: "add",
      path: "/persistent/static/metadata/createdAt",
      value: timestamp,
    });
    changes.push({
      op: "add",
      path: "/persistent/dynamic/metadata/updatedAt",
      value: timestamp,
    });
    changes.push({
      op: "add",
      path: "/nonpersistent/dynamic/metadata/lastSent/members/updatedAt",
      value: timestamp,
    });
    changes.push({
      op: "add",
      path: "/persistent/static/metadata/type",
      value: "friend",
    });
    changes.push({
      op: "add",
      path: `/persistent/static/metadata/members/users/${localStorage.getItem(
        "RID"
      )}`,
      value: {
        beamId: user?.beamId || `User#${localStorage.getItem("RID")}`,
        role: "write",
      },
    });
    changes.push({
      op: "add",
      path: `/persistent/static/metadata/members/users/${friendUserId}`,
      value: {
        beamId: friendUser?.beamId || `User#${friendUserId}`,
        role: "write",
      },
    });
    changes.push({
      op: "add",
      path: "/partitions",
      value: [`org:${localStorage.getItem("selectedOrg") || "cudb-live"}`],
    });
    console.log('create friend page', changes);
    try {
      const response = await createPage({
        data: { changes },
        topic: getCreatePagePublishTopic(),
      });
      console.log('create reponse', response);
      if (response && response.status && response.data?.id) {
        // Subscribe to the new page and request its full content.
        // socket.subscribeToEvent(
        //   SocketTopics.SubscribePageTopics,
        //   getPageSubscriptionTopic()
        // );
        // socket.publishToEvent(
        //   SocketTopics.PublishPageTopics,
        //   getPageRequestPublishTopic(),
        //   {
        //     id: response.data.id,
        //     data: {
        //       objectId: response.data.id,
        //       paths: ["$"],
        //       returnPaths: ["$"],
        //       resultType: "value",
        //     },
        //   }
        // );
      } else {
        // Allow a retry for this friend since the create didn't go through.
        inFlightFriendPages.current.delete(friendUserId);
        console.log("Create friend page error: ", response?.message || response);
      }
    } catch (error) {
      // Network/unexpected error - allow a retry for this friend.
      inFlightFriendPages.current.delete(friendUserId);
      console.log("Create friend page error: ", error);
    }
  };

  useEffect(() => {
    if (friendSearchPage) {
      const friendUserId = friendSearchPage.friendUserId;
      if (friendSearchPage.pages.length === 0) {
        // Skip if a create for this friend is already in flight (e.g. double click).
        if (!inFlightFriendPages.current.has(friendUserId)) {
          inFlightFriendPages.current.add(friendUserId);
          createFriendPage(friendUserId);
        }
      } else {
        // Page exists now; clear the guard so a future create (if the page is
        // removed) can run again.
        inFlightFriendPages.current.delete(friendUserId);
        dispatch({
          type: "SELECT_FRIEND_PAGE",
          payload: friendSearchPage.pages?.[0],
        });
      }
    }
  }, [friendSearchPage]);

  useEffect(() => {
    dispatch(GET_USERS_LIST());
    if (!getLocalStorageItem(TOKEN)) {
      history.push("/auth/login");
    }
    getCurrentLocation();
    const query = new URLSearchParams(search);
    setQueryPageId(query.get("id"));
  }, []);

  const setSelectedPage = (item: SelectedSearchItem | undefined) => {
    setScreens({
      ...screens,
      map: true,
      message: true,
      rxPanel: false,
      ridePanel: false,
      active: "",
    });
    dispatch({ type: "SET_SELECTED_ITEM", payload: item });
  };

  useEffect(() => {
    if ((pageId || queryPageId) && searchResult && searchResult.length > 0) {
      const page = searchResult.find(
        (item) =>
          item.pageId.toString() === pageId ||
          item.pageId.toString() === queryPageId
      );
      setSelectedPage(page);
    }
    console.log('query page id', pageId)
    if(pageId){
      setStep(DashboardTabs.Chat);
    }
  }, [pageId, searchResult, queryPageId]);

  useEffect(() => {
    if (selectedItem?.pageId) {
      setScreens({
        ...screens,
        map: true,
        message: true,
        ridePanel: false,
        active: "",
      });
    }
  }, [selectedItem]);

  const searchPageQuery = (offset = 0, limit = 10, searchArgs = undefined) => {
    const searchArguments = getPageSearchArgument(
      searchArgs?.search,
      searchArgs?.args,
      projects
    );
    const {
      load,
      searchIndex,
      searchQuery,
      sortByProperties,
      filterExpression,
    } = searchArguments;

    const requestId = generateUUID();
    localStorage.setItem("pageRequestId", requestId);
    const serviceId = localStorage.getItem("selectedOrg") || "cudb-live";
    console.log("service id", serviceId);
    console.log("projects", projects);
    const pageRequest = {
      select: {
        index: {
          searchIndex,
          searchQuery:
            searchQuery +
            (serviceId !== "cudb-live"
              ? `@partitions:{'org\\:${formatForRedis(serviceId)}'}`
              : ""),
          searchOptions: "TIMEOUT 10000",
        },
        // Only pin the deep-linked page when nothing is being searched —
        // otherwise every search would be restricted to that single page
        // (mobile always sends an empty list here).
        list:
          (pageId || queryPageId) && !searchArgs?.search
            ? { objectIds: [pageId || queryPageId] }
            : {},
        load,
      },
      apply: {
        expression: "",
        name: "",
      },
      result: {
        pagination: {
          offset,
          limit,
        },
      },
      sortBy: {
        properties: sortByProperties,
        max: offset + limit,
      },
      filter: {
        expression: filterExpression,
      },
      anyStatus: false,
    };

    console.log("page request", pageRequest);

    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
        "CID"
      )}/service/bds/aggregateBds`,
      wrapRequestPayload(pageRequest, requestIds.page)
    );
  };

  const aggregateUser = (inputQueryId: string, filterText?: string) => {
    const requestId = generateUUID();
    localStorage.setItem("userRequestId", requestId);
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
        "CID"
      )}/service/bds/aggregateBds`,
      wrapRequestPayload(
        {
          select: {
            index: {
              searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:userNameIndex`,
              searchQuery: "*",
              searchOptions: "TIMEOUT 10000",
            },
            list: {
              inputQueryId: inputQueryId,
            },
            load: {
              objectId: "$.objectId",
              liveObject: "$.persistent.static",
              status: "$.nonpersistent.static.live.status",
            },
          },
          groupBy: {
            properties: ["@objectId"],
            reducers: [
              {
                func: "TOLIST",
                arguments: ["@liveObject"],
                name: "profile",
              },
            ],
          },
          apply: {
            expression: "",
            name: "",
          },
          filter: {
            expression: "",
          },
          result: {
            pagination: {
              offset: 0,
              limit: 10000,
            },
          },
          anyStatus: false,
        },
        requestIds.user
      )
    );
  };

  useEffect(() => {
    if (findUsersToPage) {
      aggregateUser(findUsersToPage.queryId);
    }
  }, [findUsersToPage]);

  const findUsers = (serviceId) => {
    const queryId = generateUUID();
    const cudbServiceId = localStorage.getItem("selectedOrg") || "cudb-live";
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
        "CID"
      )}/service/live/findUsersToPage`,
      wrapRequestPayload(
        {
          userId: localStorage.getItem("RID"),
          cudbServiceId,
          modes: [
            ...Object.assign(
              [],
              cudbServiceId && cudbServiceId !== "cudb-live"
                ? ["cudb"]
                : ["liveMember", "liveFriend"]
            ),
          ],
        },
        queryId
      )
    );
  };

  const getAdminRoles = (adminUserId, cudbServiceId) => {
    const searchTopic = `${process.env.REACT_APP_ECO_SYSTEM
      }/connection/${localStorage.getItem(
        "CID"
      )}/service/${cudbServiceId}/searchInUser`;

    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      searchTopic,
      wrapRequestPayload({
        ...getUserRequestPayload(adminUserId, "$", "$..role"),
      }),

      // adminRolesTopic,
      () => { }
    );
    socket.subscribeToEvent(
      SocketTopics.SubscribePageTopics,
      searchTopic + "/reply"
    );
  };

  useEffect(() => {
    if (projects) {
      if (projects?.adminUserId) {
        getAdminRoles(projects?.adminUserId, projects?.userServiceId);
        console.log('projetcs', projects);
      }
      findUsers([projects?.userServiceId]);
      // let serviceId = "cudb-live";
      // projects?.serviceIds?.map((id) => {
      //   if (id?.[0] !== "cudb-live" && id?.[0] !== "cudb-root") {
      //     serviceId = id?.[0];
      //   }
      // });
      dispatch(GET_USER_AGGREGATE_INFO({ serviceId: localStorage.getItem("selectedOrg") || "cudb-live" }));
    }
  }, [projects]);

  const getPermission = async (messaging) => {
    return new Promise((resolve, reject) => {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          resolve(true);
        } else {
          pushNotification(
            "You have blocked notifications in your browser. Please enable it in settings",
            "error",
            "TOP_CENTER",
            2000
          );
          resolve(false);
        }
      });
    });
  };

  const handleNotification = (payload) => {
    const { title, body, icon } = payload.notification;
    browserNotifyIfInactive(title, body, icon);
  };

  const browserNotifyIfInactive = (title, body, icon, page = null) => {
    const notification = new Notification(title, {
      body,
      icon,
      requireInteraction: true,
      tag: "beam",
    });
    notification.addEventListener("click", () => {
      window.focus();
      notification.close();
    });
  };

  useEffect(() => {
    if (socket.client) {
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getPagesSubscriptionTopic()
      );
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getUserSubscriptionTopic()
      );
      // socket.subscribeToEvent(
      //   SocketTopics.SubscribePageTopics,
      //   getPageFocusSubscriptionTopic()
      // );
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getCreatePageSubscriptionTopic()
      );

      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getUpdatePageSubscriptionTopic()
      );

      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getProjectsSubscriptionTopic()
      );


      const cudbServiceId = localStorage.getItem("selectedOrg") || "cudb-live";

      socket.publishToEvent(
        SocketTopics.PublishPageTopics,
        getProjectsRequestPublishTopic(),
        wrapRequestPayload({
          userId: localStorage.getItem("RID"),
          cudbServiceId:
            cudbServiceId !== "cudb-live"
              ? cudbServiceId
              : undefined,
        })
      );

      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
          "CID"
        )}/service/iot-service/getLastIndex/reply`
      );

      // searchPageQuery();

      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
          "CID"
        )}/service/live/findUsersToPage/reply`
      );

      // iot testing
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        `${process.env.REACT_APP_ECO_SYSTEM}/connection/20036/service/iot-service/append`
      );

      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
          "CID"
        )}/service/bds/searchBds/reply`
      );

      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
          "CID"
        )}/service/bds/aggregateBds/reply`
      );

      const app = initializeApp(firebaseConfig);
      const messaging: any = getMessaging(app);

      setTimeout(async () => {
        if (await getPermission(messaging)) {
          getToken(messaging, {})
            .then((currentToken) => {
              if (currentToken) {
                let changes = [
                  {
                    op: "add",
                    path: `/nonpersistent/static/live/fcmToken`,
                    value: {
                      token: currentToken,
                      deviceType: "web",
                      deviceName: "web",
                      userId: localStorage.getItem("RID"),
                    },
                  },
                ];
                socket.publishToEvent(
                  SocketTopics.PublishPageTopics,
                  `${process.env.REACT_APP_ECO_SYSTEM
                  }/connection/${localStorage.getItem(
                    "CID"
                  )}/service/cudb-live/updateDevice`,
                  wrapRequestPayload({
                    // objectId: localStorage.getItem('RID'),
                    alternativeIds: [
                      {
                        udid: localStorage.getItem("UUID"),
                      },
                    ],
                    changes,
                  })
                );
                // ...
              } else {
                // Show permission request UI
                console.log(
                  "No registration token available. Request permission to generate one."
                );
                // ...
              }
            })
            .catch((err) => {
              console.log("An error occurred while retrieving token. ", err);
              // ...
            });

          onMessage(messaging, (payload) => {
            handleNotification(payload);
          });
        }
      });
    }
  }, [socket.client]);

  useEffect(() => {
    if (searchArgs && isMounted) {
      searchPageQuery(searchPage.offset, searchPage.limit, searchArgs);
    }
  }, [searchArgs]);

  useEffect(() => {
    if (projects) {
      searchPageQuery();
    }
  }, [projects]);

  useEffect(() => { }, [socket.client]);

  useEffect(() => {
    if (focusItem) {
      // setStep(DashboardTabs.Chat);
      // dispatch({ type: "SET_SELECTED_ITEM", payload: focusItem });
      // dispatch({
      //   type: "SET_MESSAGE_FILTER",
      //   payload: [],
      // });
    }
  }, [focusItem]);

  useEffect(() => {
    setCallNotificaitonDisplayed(!!callNotification);
    if (callNotification) {
      setCallingPageId(callNotification?.page?.id);
    } else {
      setCallingPageId(undefined);
    }
  }, [callNotification]);

  const handleCallAccept = (page: any) => {
    // const user = allUsers?.find(
    //   (obj) => obj.id === localStorage.getItem("RID")
    // );

    setCallNotificaitonDisplayed(false);
    const livekitUrl = `${process.env.REACT_APP_VIDEO_URI}?pageId=${page.objectId || page.id || page.videoRoomId}&u=${localStorage.getItem("RID")}&o=${localStorage.getItem("selectedOrg") || "cudb-live"}&metadata=${localStorage.getItem("RID")}`
    window.open(
      livekitUrl,
      "_blank"
    );

    // dispatch(
    //   ENCRYPT_TOKEN({
    //     data: {
    //       payload: JSON.stringify({
    //         userId: localStorage.getItem("RID"),
    //         userBeamId: user?.beamId,
    //         connectionId: `${localStorage.getItem("CID")}`,
    //         pageId: page.id || page.videoRoomId,
    //         pageTitle:
    //           page?.page?.persistent?.static?.metadata?.title ||
    //           (page?.page?.persistent?.static?.metadata?.type === "friend" &&
    //             getFriendUserInfo(
    //               allUsers,
    //               page?.page?.persistent?.static?.metadata?.members?.users
    //             )?.beamId
    //             ? getFriendUserInfo(
    //               allUsers,
    //               page?.page?.persistent?.static?.metadata?.members?.users
    //             )?.beamId
    //             : `Page ${page?.pageId}`),
    //       }),
    //     },
    //     callback: (token) => {
    //       setCallNotificaitonDisplayed(false);
    //       window.open(
    //         `${process.env.REACT_APP_VIDEO_URI}/room/${token.encryptedPayload}`,
    //         "_blank"
    //       );
    //     },
    //   })
    // );
  };

  const handleCallDecline = () => {
    setCallNotificaitonDisplayed(false);
  };

  const fixedColSize = pageId || queryPageId ? 12 : 8;

  const handlePageSelection = (item: SelectedSearchItem) => {
    if (!isMobileScreen) return;
    setStep(DashboardTabs.Chat);
    setSelectedPageItem(item);
  };

  // Flagged right after a page is created so the new page can be opened as soon
  // as it comes back from the server, with the participants modal on top of it.
  const onPageCreated = () => {
    dispatch({ type: "CLEAR_CREATED_PAGE" });
    setAwaitingNewPage(true);
  };

  useEffect(() => {
    if (!awaitingNewPage || !createdPageId) return;
    const page = searchResult?.find(
      (item: SelectedSearchItem) => item?.pageId?.toString() === createdPageId
    );
    if (!page) return;
    setAwaitingNewPage(false);
    dispatch({ type: "CLEAR_CREATED_PAGE" });
    setSelectedPage(page);
    dispatch({ type: "SET_MESSAGE_FILTER", payload: [] });
    handlePageSelection(page);
    setAddParticipantsVisible(true);
  }, [awaitingNewPage, createdPageId, searchResult]);
  useEffect(() => {
    if (step === DashboardTabs.Pages) setSelectedPageItem(null);
  }, [step]);

  return (
    <Row className="main-layout">
      {isCallNotificationDisplayed && (
        <CallNotification
          page={callNotification?.page}
          sender={callNotification?.sender}
          onAccept={handleCallAccept}
          onDecline={handleCallDecline}
          allUsers={allUsers}
        />
      )}
      <div className="step-navigator">
        {!pageId && step > DashboardTabs.Pages ? (
          <div>
            <Button
              onClick={() => handleChangeStep("before")}
              className="beam-btn no-shadow bg-white p-1 pr-3"
              size="small"
            >
              <LeftOutlined className="mt-1" />
              {step == DashboardTabs.Chat && (
                <span className="mt-1">Pages</span>
              )}
              {step == DashboardTabs.Map && <span className="mt-1">Chat</span>}
            </Button>
          </div>
        ) : (
          <div></div>
        )}
        {selectedPageItem && step < DashboardTabs.Map ? (
          <div>
            <Button
              onClick={() => handleChangeStep("next")}
              className="beam-btn no-shadow bg-white p-1 pl-3"
              size="small"
            >
              {step == DashboardTabs.Pages && (
                <span className="mt-1">Chat</span>
              )}
              {step == DashboardTabs.Chat && <span className="mt-1">Map</span>}
              <RightOutlined className="mt-1" />
            </Button>
          </div>
        ) : (
          <div></div>
        )}
      </div>

      {(((isMobileScreen || screens.iframe) &&
        (step == DashboardTabs.Pages ||
          step === DashboardTabs.Chat ||
          step === DashboardTabs.Map)) ||
        (!isMobileScreen && !screens.iframe)) &&
        !pageId &&
        !queryPageId && (
          <Col
            md={8}
            xs={24}
            className={screens.iframe ? "iframe-main-layout" : "side-column"}
          >
            <HeaderPanel socket={socket} />

            {screens.iframe && (
              <div className="iframe-navigator">
                {step > DashboardTabs.Pages ? (
                  <span onClick={() => handleChangeStep("before")}>
                    <LeftOutlined />{" "}
                    {step === DashboardTabs.Chat
                      ? " Pages"
                      : step === DashboardTabs.Map
                        ? " Chat"
                        : ""}
                  </span>
                ) : (
                  <div></div>
                )}
                {step < DashboardTabs.Map ? (
                  <span onClick={() => handleChangeStep("next")}>
                    {step === DashboardTabs.Pages
                      ? "Chat "
                      : step === DashboardTabs.Chat
                        ? "Map "
                        : ""}{" "}
                    <RightOutlined />
                  </span>
                ) : (
                  <div></div>
                )}
              </div>
            )}

            {step === DashboardTabs.Pages && (
              <SearchPanel
                callingPageId={callingPageId}
                socket={socket}
                params={params}
                selectedTab={step}
                onPageSelection={handlePageSelection}
                onPageCreated={onPageCreated}
              />
            )}
          </Col>
        )}

      {!screens.iframe ? (
        <React.Fragment>
          {((isMobileScreen && step == DashboardTabs.Chat) ||
            !isMobileScreen) &&
            (screens.message || screens.ridePanel) && (
              <Col
                md={screens.map ? fixedColSize : 16}
                xs={24}
                className={
                  screens.active === "message" ||
                    screens.active === "rideDispatch" ||
                    screens.active === "rxPanel"
                    ? "active-screen"
                    : ""
                }
              >
                {screens.message && (
                  <MessagePanel socket={socket} params={params} />
                )}
                {screens.ridePanel && (
                  <RidePanel type={screens.cadType} params={params} />
                )}
              </Col>
            )}
          {((isMobileScreen && step == DashboardTabs.Map) || !isMobileScreen) &&
            screens.map && (
              <Col
                md={screens.message || screens.ridePanel ? fixedColSize : 16}
                xs={24}
                className={screens.active === "map" ? "active-screen" : ""}
              >
                {showMapColumn ? (
                  <MapPanel socket={socket} params={params} />
                ) : (
                  <FilteredPanel socket={socket} params={params} />
                )}
              </Col>
            )}
        </React.Fragment>
      ) : (
        <Col
          md={16}
          className={`iframe-container ${screens?.iframeUrl?.includes("dashboard") ? "dashboard" : "cad"
            }`}
        >
          <iframe
            src={`${screens?.iframeUrl}&lat=${location?.lat}&lng=${location?.long}`}
            title="Shared"
          ></iframe>
        </Col>
      )}

      <Autocomplete
        apiKey="AIzaSyDgNagv_ozKWc6IQ75Ol4cH8fxdntOaW6M"
        style={{ display: "none" }}
        options={{
          types: ["(regions)"],
          componentRestrictions: { country: ["us", "ca", "uk"] },
        }}
      />
      {videoPlayerOpen && (
        <VideoPlayerModal
          url={twilioVideoURL}
          onClose={() => setVideoPlayerOpen(false)}
        />
      )}
      {addParticipantsVisible && (
        <PeopleModal
          socket={socket}
          onClose={() => setAddParticipantsVisible(false)}
        />
      )}
      <FirstCADIframeModal
        isModalVisible={firstCADVisible}
        onClose={() => {
          setFirstCADVisible(false);
        }}
      />
    </Row>
  );
};

export default Dashboard;
